// ./src/hooks/useDisplayActivations.ts
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import {
  DisplayActivation,
  DisplayActivationInput,
  DisplayActivationFilter,
  DisplayActivationService,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseDisplayActivationsOptions {
  /** Display ID to initialize activation for or filter by */
  displayId?: string | undefined | null;

  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<DisplayActivationFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Mode: 'single' for single activation operations, 'list' for CRUD operations */
  mode?: "single" | "list";
}

export interface DisplayActivationData {
  displayId: string;
  activationCode: string;
  isActivated: boolean;
  expiresAt: string;
}

export interface UseDisplayActivationsReturn {
  // Data state
  displayActivation: DisplayActivation | null;
  displayActivations: DisplayActivation[];
  activationData: DisplayActivationData | null;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;
  isInitialized: boolean;

  // Search and filters
  filters: Partial<DisplayActivationFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isUpdating: boolean;
  removingActivationId: string | null;
  selectedActivation: DisplayActivation | null;

  // Core operations (matching API method names exactly)
  list: (
    filter?: Partial<DisplayActivationFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  update: (
    id: string,
    params: DisplayActivationInput
  ) => Promise<DisplayActivation | null>;
  remove: (id: string) => Promise<boolean>;

  // Special activation operations (unique to this hook)
  init: (params: DisplayActivationInput) => Promise<void>;
  link: (params: DisplayActivationInput) => Promise<OpResult>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<DisplayActivationFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedActivation: (activation: DisplayActivation | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values (for single mode)
  isActivated: boolean;
  isExpired: boolean;
  activationCode: string | null;
  expiresIn: number | null; // milliseconds until expiration

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "createdAt", direction: "desc" }];

export const useDisplayActivations = (
  options: UseDisplayActivationsOptions = {}
): UseDisplayActivationsReturn => {
  const {
    displayId,
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = false,
    mode = "single",
  } = options;

  // Use refs for option values to avoid dependency issues
  const displayIdRef = useRef(displayId);
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    displayIdRef.current = displayId;
  }, [displayId]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [displayActivation, setDisplayActivation] =
    useState<DisplayActivation | null>(null);
  const [displayActivations, setDisplayActivations] = useState<
    DisplayActivation[]
  >([]);
  const [activationData, setActivationData] =
    useState<DisplayActivationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<DisplayActivationFilter>>(
    () => {
      const filter = { ...initialFilters };
      if (displayId) {
        filter.displayId = [displayId];
      }
      return filter;
    }
  );
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingActivationId, setRemovingActivationId] = useState<
    string | null
  >(null);
  const [selectedActivation, setSelectedActivation] =
    useState<DisplayActivation | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => displayActivations.length === 0 && !loading,
    [displayActivations.length, loading]
  );

  // Helper function to detect device info
  const getDeviceInfo = useCallback(() => {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const resolution = `${screen.width}x${screen.height}`;

    return {
      userAgent,
      platform,
      resolution,
    };
  }, []);

  // Helper function to update activation in list if it exists, or add it if not
  const updateOrAddActivationInList = useCallback(
    (updatedActivation: DisplayActivation) => {
      setDisplayActivations((prev) => {
        const existingIndex = prev.findIndex(
          (a) => a.id === updatedActivation.id
        );
        if (existingIndex >= 0) {
          const newActivations = [...prev];
          newActivations[existingIndex] = updatedActivation;
          return newActivations;
        } else {
          return [updatedActivation, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove activation from list if it exists
  const removeActivationFromList = useCallback((activationId: string) => {
    setDisplayActivations((prev) => prev.filter((a) => a.id !== activationId));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setDisplayActivation(null);
    setDisplayActivations([]);
    setActivationData(null);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsUpdating(false);
    setRemovingActivationId(null);
    setSelectedActivation(null);
  }, []);

  // List display activations (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<DisplayActivationFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setDisplayActivations([]);
          setCurrentPage(1);
        }
      }

      const combinedFilter = { ...filter };
      if (displayIdRef.current && !combinedFilter.displayId) {
        combinedFilter.displayId = [displayIdRef.current];
      }

      setFilters(combinedFilter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await DisplayActivationService.list(
          combinedFilter as DisplayActivationFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newActivations = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setDisplayActivations((prev) => {
              const existingIds = new Set(prev.map((a) => a.id));
              const uniqueNewActivations = newActivations.filter(
                (a) => !existingIds.has(a.id)
              );
              return [...prev, ...uniqueNewActivations];
            });
          } else {
            setDisplayActivations(newActivations);
          }

          setHasMore(newActivations.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load display activations";
          setLastError(errorMessage);
          if (!isAppending) setDisplayActivations([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load display activations";
        setLastError(errorMessage);
        if (!isAppending) setDisplayActivations([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Update display activation (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: DisplayActivationInput
    ): Promise<DisplayActivation | null> => {
      if (!id) {
        setLastError("Display activation ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await DisplayActivationService.update(id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedActivation = result.data[0] as DisplayActivation;
          setDisplayActivation(updatedActivation);
          updateOrAddActivationInList(updatedActivation);
          setLastError(null);
          return updatedActivation;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to update display activation";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update display activation";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddActivationInList]
  );

  // Remove display activation (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Display activation ID is required for removal");
        return false;
      }

      setRemovingActivationId(id);
      setLastError(null);

      try {
        const result = await DisplayActivationService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setDisplayActivation(null);
          removeActivationFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove display activation";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove display activation";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingActivationId(null);
      }
    },
    [removeActivationFromList]
  );

  // Initialize activation function (special operation)
  const init = useCallback(
    async (params: DisplayActivationInput) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await DisplayActivationService.init({
          ...params,
          ...getDeviceInfo(),
          appVersion: import.meta.env.VITE_APP_VERSION,
        });

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newActivationData = result.data[0] as DisplayActivationData;
          setActivationData(newActivationData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to initialize display activation";
          setLastError(errorMessage);
          setActivationData(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to initialize display activation";
        setLastError(errorMessage);
        setActivationData(null);
      } finally {
        setLoading(false);
      }
    },
    [getDeviceInfo]
  );

  useEffect(() => {
    console.log("Last error has changed to", lastError);
  }, [lastError]);

  // Link display function (special operation)
  const link = useCallback(
    async (params: DisplayActivationInput) => {
      setLoading(true);
      setLastError(null);

      let result = new OpResult();

      try {
        result = await DisplayActivationService.link({
          ...params,
          ...getDeviceInfo(),
        });

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newActivationData = result.data[0] as DisplayActivationData;
          setActivationData(newActivationData);
          setLastError(null);
        } else {
          const errorMessage =
            result.getErrorSummary() || "Failed to link display activation";
          setLastError(errorMessage);
          setActivationData(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to link display activation";
        setLastError(errorMessage);
        setActivationData(null);

        result.addError("", errorMessage);
      } finally {
        setLoading(false);
      }

      return result;
    },
    [getDeviceInfo]
  );

  // Load more display activations
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh display activations
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && !hasAutoLoaded) {
      setHasAutoLoaded(true);

      if (mode === "single" && displayId) {
        init({ displayId });
      } else if (mode === "list") {
        const autoLoadFilters = { ...initialFilters };
        if (displayId) {
          autoLoadFilters.displayId = [displayId];
        }
        list(autoLoadFilters, { page: 1, pageSize }, initialSort, true);
      }
    }
  }, [autoLoad, hasAutoLoaded, mode, displayId, pageSize, initialSort]);

  // Computed values for single mode
  const isInitialized = activationData !== null;
  const isActivated = activationData?.isActivated ?? false;
  const activationCode = activationData?.activationCode ?? null;

  const isExpired = useMemo(() => {
    if (!activationData?.expiresAt) return false;
    return new Date(activationData.expiresAt) <= new Date();
  }, [activationData?.expiresAt]);

  const expiresIn = useMemo(() => {
    if (!activationData?.expiresAt) return null;
    const expirationTime = new Date(activationData.expiresAt).getTime();
    const currentTime = Date.now();
    const timeLeft = expirationTime - currentTime;
    return timeLeft > 0 ? timeLeft : 0;
  }, [activationData?.expiresAt]);

  return {
    displayActivation,
    displayActivations,
    activationData,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    isInitialized,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isUpdating,
    removingActivationId,
    selectedActivation,
    list,
    update,
    remove,
    init,
    link,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedActivation,
    reset,
    isActivated,
    isExpired,
    activationCode,
    expiresIn,
    lastError,
    clearError,
  };
};
